import * as yup from 'yup';
import { AmsPermissionClaimType, ArmsPermissionClaimType, authService } from '../../services/Auth';
import { Card, CardBody, Col, Form, FormGroup, Row } from 'reactstrap';
import { NavLink, Redirect } from 'react-router-dom';
import React, { ReactText, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../../store';

import Button from '../../components/Button';
import Input from '../../components/Input';
import { IAuthState } from '../../store/auth/Types';
import Logo from '../../assets/images/logo-blue.png';
import { login } from '../../store/auth/Actions';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// Form data interface
interface LoginFormData {
    email: string;
    password: string;
}

// Validation schema
const loginSchema = yup.object({
    email: yup.string().required('Email is required').email('Please enter a valid email'),
    password: yup.string().required('Password is required').min(1, 'Password is required'),
});

const Login = () => {
    const auth: IAuthState = useSelector((state: any) => state.auth);
    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
    } = useForm<LoginFormData>({
        resolver: yupResolver(loginSchema),
        mode: 'onChange',
        defaultValues: {
            email: '',
            password: '',
        },
    });
    const dispatch = useDispatch<AppDispatch>();
    const [toastId, setToastId] = useState('' as ReactText);
    const [redirect] = useState(`${localStorage.getItem('redirect') ? localStorage.getItem('redirect') : ''}`);
    const onSubmit = useCallback(
        (formData: LoginFormData) => {
            // Dismiss any existing toast first
            if (toast.isActive(toastId)) {
                toast.dismiss(toastId);
            }

            // Always create a new toast for each login attempt
            const newToastId = toast.info('Checking Credentials...', {
                autoClose: false,
                closeOnClick: false,
                closeButton: false,
            });
            setToastId(newToastId);

            dispatch(login(formData));
        },
        [dispatch],
    );

    useEffect(() => {
        if (toast.isActive(toastId)) {
            if (auth.authenticated) {
                toast.update(toastId ?? '', {
                    type: 'success',
                    render: 'Login successful.',
                    autoClose: 2000,
                    closeOnClick: true,
                    draggable: true,
                });
            } else if (auth.authenticationError) {
                toast.update(toastId ?? '', {
                    type: 'error',
                    render: auth.authenticationError.includes('timeout')
                        ? 'Login timeout - please try again'
                        : 'Invalid credentials.',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                });
            }
        }
    }, [auth, toastId]);

    useEffect(() => {
        localStorage.removeItem('redirect');
    }, [redirect]);

    const redirectToUrl = useCallback(() => {
        if (redirect) {
            return redirect;
        }

        return authService.hasPermission(AmsPermissionClaimType, 'systemadmin')
            ? '/Institutions'
            : authService.hasPermission(ArmsPermissionClaimType, 'admin')
              ? '/annual-report/overview'
              : authService.hasPermission(ArmsPermissionClaimType, 'viewannualreport')
                ? '/annual-report/dashboard'
                : authService.hasPermission(AmsPermissionClaimType, 'hubviewdocuments')
                  ? '/hub/folders/main'
                  : '/profile';
    }, [redirect]);

    return (
        <>
            {auth.authenticated && <Redirect to={redirectToUrl()} />}
            <Row className="justify-content-center">
                <Col md="8" lg="6" xl="5">
                    <Card>
                        <CardBody>
                            <div className="text-center w-75 m-auto">
                                <NavLink to="/">
                                    <span>
                                        <img src={Logo} alt="logo" height="150" />
                                    </span>
                                </NavLink>
                                <p className="text-muted mb-4 mt-3">Enter your email address and password.</p>
                            </div>

                            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                                <FormGroup className="mb-3">
                                    <label htmlFor="email">Email address</label>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                className="form-control"
                                                type="email"
                                                id="email"
                                                placeholder="Enter your email"
                                            />
                                        )}
                                    />
                                    {errors.email && <span className="text-danger">{errors.email.message}</span>}
                                </FormGroup>
                                <FormGroup className="mb-3">
                                    <label htmlFor="password">Password</label>
                                    <Controller
                                        name="password"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                className="form-control"
                                                type="password"
                                                id="password"
                                                placeholder="Enter your password"
                                            />
                                        )}
                                    />
                                    {errors.password && <span className="text-danger">{errors.password.message}</span>}
                                </FormGroup>
                                <FormGroup className="mb-0">
                                    <Button className="btn-block w-100" type="submit">
                                        Log In
                                    </Button>
                                </FormGroup>
                            </Form>
                        </CardBody>
                    </Card>

                    <Row className="mt-3">
                        <Col className="text-center">
                            <p>
                                <NavLink to="/passwordreset/request" className="ms-1">
                                    Forgot your password?
                                </NavLink>
                            </p>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </>
    );
};

export default Login;
